import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import { FileText, Settings, UserCircle2, Users, SlidersHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { supabase } from '../lib/supabaseClient';
import { fetchProfileLookups, hydrateProfile } from '../lib/profileLookups';

function SettingsPage() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate('/');
      return;
    }

    const { data: profileData, error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error || !profileData) {
      setErrorMsg('Profile user tidak ditemukan.');
      setLoading(false);
      return;
    }

    if (!profileData.is_active) {
      setErrorMsg('Akun nonaktif.');
      setLoading(false);
      return;
    }

    try {
      const lookups = await fetchProfileLookups(supabase);
      setProfile(hydrateProfile(profileData, lookups));
    } catch (lookupError) {
      setErrorMsg(lookupError.message);
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const menuItems = useMemo(() => {
    const accountMenu = [
      {
        title: 'Akun Saya',
        icon: UserCircle2,
        path: '/settings/account',
      },
    ];

    if (profile?.role !== 'superadmin') return accountMenu;

    return [
      ...accountMenu,
      {
        title: 'Pengguna',
        icon: Users,
        path: '/settings/users',
      },
      {
        title: 'Sistem',
        icon: SlidersHorizontal,
        path: '/settings/system',
      },
      {
        title: 'Kriteria',
        icon: FileText,
        path: '/settings/kriteria',
      },
    ];
  }, [profile]);

  if (loading) {
    return (
      <DashboardLayout
        title="Pengaturan"
        breadcrumbs={['Dashboard', 'Pengaturan']}
        profile={profile}
        activeMenu="settings"
      >
        <div className="page-loading-v2">
          <Spinner animation="border" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Pengaturan"
      breadcrumbs={['Dashboard', 'Pengaturan']}
      profile={profile}
      activeMenu="settings"
    >
      <Container fluid className="p-0">
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

        <Card className="settings-hero-card mb-3">
          <Card.Body>
            <div className="settings-hero-icon">
              <Settings size={26} />
            </div>
            <div>
              <div className="hero-eyebrow-v2">Pengaturan Sistem</div>
              <h2 className="settings-hero-title">Pengaturan</h2>
            </div>
          </Card.Body>
        </Card>

        <Row className="g-3">
          {menuItems.map((item) => {
            const Icon = item.icon;

            return (
              <Col xl={4} md={6} key={item.title}>
                <button
                  type="button"
                  className="settings-menu-card"
                  onClick={() => navigate(item.path)}
                >
                  <div className="settings-menu-icon">
                    <Icon size={24} />
                  </div>
                  <div>
                    <div className="settings-menu-title">{item.title}</div>
                  </div>
                </button>
              </Col>
            );
          })}
        </Row>
      </Container>
    </DashboardLayout>
  );
}

export default SettingsPage;
